import { Component, computed, OnDestroy, Signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PlaygroundService, EndpointCatalogService, NotificationService } from '../../../core/services';
import { EndpointDefinition, getEndpointDefinition } from '../../../core/models';

import { LegalResult } from '../models/legal-result.model';
import { buildCodeSnippets, CodeLanguage } from '../utils/code-snippet';
import { estimateEndpointPrice, PriceEstimate } from '../utils/endpoint-pricing';
import { PlaygroundQueryPage } from '../utils/playground-query-page';
import { PlaygroundKeyService } from '../utils/playground-key.service';

@Component({
  selector: 'app-legal',
  standalone: false,
  templateUrl: './legal.html',
  styleUrl: './legal.scss',
})
export class Legal implements OnDestroy, PlaygroundQueryPage {
  constructor(
    private fb: FormBuilder,
    private playgroundService: PlaygroundService,
    private catalogService: EndpointCatalogService,
    private notificationService: NotificationService,
  ) {
    this.form = this.buildForm();

    // /legal has no entry in the live catalog yet (still "Not ready"), so
    // this stays undefined and estimateEndpointPrice() returns a zero
    // estimate until the backend adds it see endpoint-catalog.model.ts.
    this.endpointDef = computed(() => getEndpointDefinition(this.catalogService.catalog(), 'legal'));

  }

  private readonly _destroy$ = new Subject<void>();

  readonly endpointDef: Signal<EndpointDefinition | undefined>;

  isLoading = false;
  status: 'idle' | 'ok' | 'error' = 'idle';
  response: LegalResult | null = null;
  latencyMs: number | null = null;
  /** Real credits consumed by the last run, as reported by the backend not an estimate. */
  credits: number | null = null;

  activeLanguage: CodeLanguage = 'ruby';
  copied = false;

  /** The key runs bill to: picked once in the run bar (playground.html). */
  private readonly apiKey = inject(PlaygroundKeyService);

  form: FormGroup;

  private buildForm(): FormGroup {
    return this.fb.group({
      domain: ['', Validators.required],
    });
  }

  // ── Pricing ──────────────────────────────────────────────────────────
  /** Live price estimate, recomputed from current form state on every
   *  change against the real catalog see utils/endpoint-pricing.ts. /legal
   *  takes no optional params today, so there's nothing to mark active. */
  get estimatedPrice(): PriceEstimate {
    return estimateEndpointPrice(this.endpointDef(), []);
  }

  get requestSummary(): string {
    return (this.form.value.domain ?? '').trim();
  }

  get codeSnippet(): string {
    return buildCodeSnippets('legal', 'legal', this.buildPayload())[this.activeLanguage];
  }

  setLanguage(lang: CodeLanguage): void {
    this.activeLanguage = lang;
  }

  copySnippet(): void {
    navigator.clipboard?.writeText(this.codeSnippet).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  private buildPayload() {
    return { domain: this.form.value.domain };
  }

  runQuery(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('Enter a domain name before running the query.');
      return;
    }

    // The Run button is disabled for this already (see playground.html) this is
    // just a backstop, since the backend can only 422 without a key to bill.
    if (!this.apiKey.keys().length) {
      this.notificationService.error('Create an API key before running this query.');
      return;
    }

    const payload = this.buildPayload();

    this.isLoading = true;
    this.playgroundService.runLegal(payload, this.apiKey.selectedId() ?? undefined)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.status = 'error';
          this.notificationService.error('An error occurred while running the query. Please try again later.');
          return throwError(error);
        }),
        tap(({ result, latencyMs, credits }) => {
          this.response = result;
          this.latencyMs = latencyMs;
          this.credits = credits;
          this.status = 'ok';
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Called from the shared Playground footer's Reset button (see
   *  playground.ts/.html) once the user confirms the confirmation
   *  prompt itself lives there, not here, since it's one shared footer
   *  for all seven playground pages. */
  resetForm(): void {
    this.form = this.buildForm();
    this.status = 'idle';
    this.response = null;
    this.latencyMs = null;
    this.credits = null;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
